"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { usePathname } from "next/navigation"
import { HelpCircle, Loader2, Send } from "lucide-react"
import { toast } from "sonner"

import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

type Ctx = "landing" | "app"

interface SupportBubbleProps {
  context?: Ctx
}

const SUPPORT_OPEN_EVENT = "support:open"

export function openSupportDialog() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SUPPORT_OPEN_EVENT))
  }
}

function detectContext(pathname: string | null): Ctx {
  if (!pathname) return "landing"
  if (pathname === "/" || pathname.startsWith("/auth")) return "landing"
  return "app"
}

export function SupportBubble({ context: forcedContext }: SupportBubbleProps = {}) {
  const pathname = usePathname()
  const context: Ctx = forcedContext ?? detectContext(pathname)

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [isAuthed, setIsAuthed] = useState(false)

  // Pré-remplir email si user connecté
  useEffect(() => {
    if (context !== "app") return
    let cancelled = false
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (cancelled) return
      if (user) {
        setIsAuthed(true)
        if (user.email) setEmail(user.email)
      }
    })
    return () => {
      cancelled = true
    }
  }, [context])

  // Listen for openSupportDialog() calls (e.g. from the header help button)
  useEffect(() => {
    const handler = () => setOpen(true)
    window.addEventListener(SUPPORT_OPEN_EVENT, handler)
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, handler)
  }, [])

  // FAB only on landing — on app context the trigger lives in AppHeader.
  const showFab = context === "landing"
  const positionClass = useMemo(() => "fixed bottom-4 right-4 z-50", [])

  const reset = useCallback(() => {
    setMessage("")
    setDone(false)
    setSubmitting(false)
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (!next) {
        setTimeout(reset, 200)
      }
    },
    [reset]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return

    const trimmed = message.trim()
    if (trimmed.length < 3) {
      toast.error("Message trop court (min 3 caractères)")
      return
    }
    if (context === "landing" || !isAuthed) {
      const emailTrim = email.trim()
      if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
        toast.error("Email valide requis")
        return
      }
    }

    setSubmitting(true)
    try {
      const res = await fetch("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          email: email.trim() || undefined,
          context,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        toast.error(data.error || "Envoi impossible")
        setSubmitting(false)
        return
      }
      setDone(true)
    } catch (err) {
      console.error("[support-bubble] submit failed", err)
      toast.error("Erreur réseau, réessaie")
      setSubmitting(false)
    }
  }

  const showEmailField = context === "landing" || !isAuthed

  return (
    <>
      {showFab && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Besoin d'aide ? Envoyer un message"
          className={`${positionClass} group relative h-13 w-13 flex items-center justify-center rounded-2xl bg-white/90 backdrop-blur-md shadow-lg shadow-black/10 ring-1 ring-black/5 hover:scale-105 hover:bg-gradient-to-br hover:from-violet-500 hover:to-fuchsia-500 transition-all duration-200`}
          style={{ width: 52, height: 52 }}
        >
          <HelpCircle className="h-6 w-6 text-violet-600 group-hover:text-white transition-colors" />
          <span className="absolute top-1 right-1 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-fuchsia-500" />
          </span>
        </button>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          {done ? (
            <div className="text-center space-y-4 py-2">
              <div className="text-5xl">🙏</div>
              <DialogHeader>
                <DialogTitle className="text-center">Merci</DialogTitle>
                <DialogDescription className="text-center">
                  On a bien reçu ton message. Yann ou Élise te répondent dans la journée.
                </DialogDescription>
              </DialogHeader>
              <Button type="button" onClick={() => handleOpenChange(false)} className="w-full">
                Fermer
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <DialogHeader>
                <DialogTitle>Un souci ? Une idée ?</DialogTitle>
                <DialogDescription>
                  Dis-nous tout, on revient vite vers toi 💌
                </DialogDescription>
              </DialogHeader>

              {showEmailField && (
                <div className="space-y-2">
                  <Label htmlFor="support-email">Ton email</Label>
                  <Input
                    id="support-email"
                    type="email"
                    placeholder="toi@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={context === "landing"}
                    disabled={submitting}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="support-message">Ton message</Label>
                <Textarea
                  id="support-message"
                  placeholder="Décris ton problème, ton idée, ou ce qui te bloque…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  minLength={3}
                  maxLength={5000}
                  required
                  disabled={submitting}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {message.length} / 5000
                </p>
              </div>

              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Envoyer
                  </>
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
